import type { UseFormReturn } from 'react-hook-form';
import type { ScheduleFormValues } from './Form';

/**
 * Putting a draft's opening values into the form, and keeping them there.
 *
 * Extracted from the fields so the order these run in can be tested. They fire
 * from two effects in the same commit, and getting that order wrong is how a
 * draft came to be initialised and then immediately undone.
 *
 * Everything here goes through `setValue` and `getValues`, which work whether
 * or not a field is currently rendered. `resetField` does not: on an
 * unregistered field it wipes the value to `undefined` *and* leaves it dirty,
 * and `notesHr` is unregistered for every viewer who is not the employee's HR.
 */

/** The parts of the form these need, so a test can pass a real one. */
export type DraftForm = Pick<
  UseFormReturn<ScheduleFormValues>,
  'getValues' | 'setValue'
>;

/** What a draft opens with, read off the records it opened on. */
export type DraftValues = {
  typeId: number | null;
  tentative: boolean;
  notes: string;
  /** The HR note, or `undefined` where this viewer is not entitled to it. */
  notesHr: string | undefined;
};

/** Seed the fields a draft opens with, once. */
export function seedDraftFields(form: DraftForm, values: DraftValues): void {
  form.setValue('typeId', values.typeId);
  form.setValue('tentative', values.tentative);
  form.setValue('notes', values.notes);
  // Left `undefined` rather than blank for anybody but HR, so the payload omits
  // the field altogether: the server reads a blank one as an instruction to
  // wipe whatever HR wrote.
  form.setValue('notesHr', values.notesHr);
}

/**
 * Whether entitlement may be acted on yet for this draft.
 *
 * Both effects fire in the same commit, and on the commit a new draft arrives
 * the form still holds the *previous* draft's employee -- the effect that
 * copies it across has not run. Entitlement asked then is asked about the wrong
 * person, and acting on the answer clears the note seeding has just written.
 *
 * So: only for a draft that has been seeded, and only once the form's employee
 * is that draft's.
 */
export function shouldFollowEntitlement({
  seededDraftId,
  draftId,
  formUserId,
  draftUserId,
}: {
  seededDraftId: number | null;
  draftId: number | null;
  formUserId: number | null;
  draftUserId: number | null;
}): boolean {
  if (draftId === null || seededDraftId !== draftId) return false;
  return formUserId === draftUserId;
}

/**
 * Follow the HR note's entitlement while a draft is open.
 *
 * Entitlement can move under a draft two ways: the employee is changed in the
 * form, or the employee list lands after the draft opened. Neither is seeding,
 * and both have to be answered without overwriting what the user is typing.
 *
 * Losing it clears the field -- that decides whether the payload carries the
 * key at all, and safety outweighs a value in an input nobody may send. Gaining
 * it fills from the record only while the field is untouched.
 */
export function applyNotesHrEntitlement(
  form: DraftForm,
  {
    entitled,
    notesHr,
    seeded,
  }: { entitled: boolean; notesHr: string; seeded: string | undefined },
): void {
  if (!entitled) {
    form.setValue('notesHr', undefined);
    return;
  }
  // Untouched means "still holding what this draft opened with", compared by
  // value. Not the form's own dirty flag: the form is not remounted between
  // drafts, so that flag survives from the draft before this one -- and it is
  // reported for a field that is not currently rendered, which `notesHr` is
  // not whenever the viewer is somebody else's HR.
  if (form.getValues('notesHr') === seeded) {
    form.setValue('notesHr', notesHr);
  }
}
